import { Phone } from "lucide-react";
import { PHONE_DISPLAY, PHONE_HREF } from "@/lib/contact";

// Curated Unsplash candidates related to skip bins / skip trucks / waste hauling.
// Aspect ratio is fine for wide hero; strong green overlay applied to all.
export const HERO_CANDIDATES = [
    {
        id: 1,
        url: "https://images.unsplash.com/photo-1728219609949-dbc14f715284?auto=format&fit=crop&w=1920&q=70",
        label: "Yellow skip bin on a street",
        credit: "Haberdoedas / Unsplash",
    },
    {
        id: 2,
        url: "https://images.unsplash.com/photo-1770390706248-5669d9c242d4?auto=format&fit=crop&w=1920&q=70",
        label: "Large skip bin next to city buildings",
        credit: "Haberdoedas / Unsplash",
    },
    {
        id: 3,
        url: "https://images.unsplash.com/photo-1711568288588-8837fb930a93?auto=format&fit=crop&w=1920&q=70",
        label: "Skip bin roadside with traffic cone (job in progress)",
        credit: "Haberdoedas II / Unsplash",
    },
    {
        id: 4,
        url: "https://images.unsplash.com/photo-1707062358488-41594b7d6290?auto=format&fit=crop&w=1920&q=70",
        label: "Big yellow tipper truck (industrial)",
        credit: "Phil Hearing / Unsplash",
    },
    {
        id: 5,
        url: "https://images.unsplash.com/photo-1673978484311-53101383b079?auto=format&fit=crop&w=1920&q=70",
        label: "Green tipper truck loaded with bricks (heavy waste)",
        credit: "d c / Unsplash",
    },
    {
        id: 6,
        url: "https://images.unsplash.com/photo-1745518420340-578f76a916db?auto=format&fit=crop&w=1920&q=70",
        label: "Green skip bin, wide open landscape",
        credit: "Lukas S / Unsplash",
    },
    {
        id: 7,
        url: "https://images.unsplash.com/photo-1771335976646-20410e782e3c?auto=format&fit=crop&w=1920&q=70",
        label: "Construction crew with barriers and cones",
        credit: "Ruben Mavarez / Unsplash",
    },
    {
        id: 8,
        url: "https://images.unsplash.com/photo-1523293182086-7651a899d37f?auto=format&fit=crop&w=1920&q=70",
        label: "Current photo — warehouse / storage (needs replacing)",
        credit: "Unsplash",
        current: true,
    },
];

export const HeroPreview = () => {
    return (
        <div className="min-h-screen bg-tp-offwhite">
            <header className="bg-tp-forest text-white py-6">
                <div className="max-w-6xl mx-auto px-4">
                    <h1 className="font-heading text-3xl md:text-4xl">
                        Hero background — pick your favourite
                    </h1>
                    <p className="mt-2 text-sm text-white/85">
                        Each option shown with the actual forest-green overlay and hero
                        text applied. Tell me the number you like and I&rsquo;ll swap it
                        into the live site. Or upload your own photo any time.
                    </p>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
                {HERO_CANDIDATES.map((c) => (
                    <section
                        key={c.id}
                        data-testid={`hero-candidate-${c.id}`}
                        className="rounded-2xl overflow-hidden border border-tp-forest/15 shadow-md bg-white"
                    >
                        <div className="px-5 py-3 bg-tp-forest text-white flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-3">
                                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-tp-gold text-tp-forest font-heading text-base">
                                    {c.id}
                                </span>
                                <div>
                                    <div className="font-semibold">
                                        Option {c.id}: {c.label}
                                    </div>
                                    <div className="text-[11px] text-white/70">
                                        {c.credit}
                                    </div>
                                </div>
                            </div>
                            {c.current && (
                                <span className="text-[11px] uppercase tracking-[0.14em] rounded-full px-2.5 py-1 bg-white/10 border border-white/20 text-tp-gold">
                                    Currently live
                                </span>
                            )}
                        </div>

                        {/* Hero simulation */}
                        <div className="relative overflow-hidden bg-tp-forest text-white">
                            <img
                                src={c.url}
                                alt={c.label}
                                className="absolute inset-0 w-full h-full object-cover opacity-55"
                                loading="lazy"
                                decoding="async"
                            />
                            <div
                                className="absolute inset-0"
                                style={{
                                    background:
                                        "linear-gradient(180deg, rgba(11,58,31,0.94) 0%, rgba(20,83,45,0.86) 55%, rgba(20,83,45,0.94) 100%)",
                                }}
                            />
                            <div className="tp-grain absolute inset-0" />
                            <div
                                className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full pointer-events-none"
                                style={{
                                    background:
                                        "radial-gradient(closest-side, rgba(232,185,35,0.35), rgba(232,185,35,0) 70%)",
                                }}
                            />

                            <div className="relative px-6 py-10 md:py-14 max-w-3xl">
                                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] text-tp-gold">
                                    <span className="h-1.5 w-1.5 rounded-full bg-tp-recycle" />
                                    Servicing Sydney, all suburbs
                                </div>
                                <h2 className="mt-4 font-heading text-3xl md:text-5xl leading-[1.02]">
                                    <span className="text-white">Skip Bin Hire Across </span>
                                    <span className="text-tp-gold">Sydney</span>
                                </h2>
                                <p className="mt-3 text-sm md:text-base text-white/90 max-w-xl">
                                    Family owned, local and affordable. Bins from 2 to 8
                                    cubic metres delivered to your door.
                                </p>
                                <div className="mt-5 flex flex-col sm:flex-row gap-3">
                                    <a
                                        href={PHONE_HREF}
                                        className="tp-btn-primary text-base h-12 px-5 justify-center"
                                    >
                                        <Phone size={18} strokeWidth={2.6} />
                                        Call {PHONE_DISPLAY}
                                    </a>
                                    <a
                                        href="#quote"
                                        className="tp-btn-outline text-base h-12 px-5 justify-center"
                                    >
                                        Get a Free Quote
                                    </a>
                                </div>
                            </div>
                        </div>
                    </section>
                ))}

                <div className="rounded-xl bg-tp-forest text-white p-5 text-sm">
                    <p className="font-semibold">Ready to choose?</p>
                    <p className="mt-1 text-white/80">
                        Reply with the option number (1&ndash;7) and I&rsquo;ll swap it
                        into the live homepage in seconds. Or upload your own photo and
                        I&rsquo;ll drop it in instead.
                    </p>
                    <div className="mt-3">
                        <a
                            href="/"
                            className="tp-btn-primary text-sm h-11"
                        >
                            &larr; Back to homepage
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HeroPreview;
